import type { Capture } from "./schema";
import type { CaptureStorage } from "./storage";
import type { SyncCaptureResult } from "./sync-capture";

export interface ResyncFailure {
  id: string;
  error: string;
  /** Motif technique brut, jamais affiché : classement de l'échec (C20). */
  detail?: string;
}

// Un abandon porte la capture COMPLÈTE (pas juste l'id) : le caller la
// journalise hors buffer (notice usager + réversibilité d'un faux positif).
export interface ResyncAbandon {
  capture: Capture;
  error: string;
}

export interface ResyncResult {
  alreadySynced: number;
  resynced: number;
  failed: number;
  // Détail des échecs TRANSITOIRES (id + message) pour l'observabilité : avant, un
  // échec de synchro n'était qu'un compteur muet (`failed++`) → captures coincées
  // invisibles. Le caller (use-resync-pending) journalise ce détail. Ces captures
  // restent en local et sont ré-essayées au prochain trigger.
  failures: ResyncFailure[];
  // Captures ABANDONNÉES : leur fichier audio local est définitivement introuvable
  // (purgé de `tmp/`, conteneur réécrit mais fichier disparu). On les RETIRE du
  // buffer — réessayer ne ressuscitera jamais le fichier ; sans ça, elles bouclent
  // et spamment le log à chaque lancement / online / 1er plan. Le caller les
  // signale une fois (elles ne reviendront pas, étant retirées).
  abandoned: ResyncAbandon[];
}

// Filet réseau : re-synchronise les captures restées dans le buffer local
// (= sauvées localement mais dont la synchro a échoué — on retire du local à la
// synchro réussie). À appeler au lancement / retour au 1er plan.
//
// - Si l'état DB est indéterminé (réseau injoignable → `fetchExistingIds` renvoie
//   `null`), on ne tente RIEN (éviter des inserts en double).
// - Une capture déjà présente en DB est simplement retirée du buffer (nettoyage
//   des copies locales d'avant ce filet, jamais re-poussée → pas d'écrasement
//   de l'état serveur).
// - Les autres sont re-synchronisées ; retirées du local à la réussite, gardées
//   sinon (ré-essai au prochain lancement).
export async function resyncPendingCaptures(
  storage: CaptureStorage,
  fetchExistingIds: () => Promise<Set<string> | null>,
  syncOne: (capture: Capture) => Promise<SyncCaptureResult>,
): Promise<ResyncResult> {
  const local = await storage.list();
  if (local.length === 0)
    return { alreadySynced: 0, resynced: 0, failed: 0, failures: [], abandoned: [] };

  const existing = await fetchExistingIds();
  if (existing === null)
    return { alreadySynced: 0, resynced: 0, failed: 0, failures: [], abandoned: [] };

  let alreadySynced = 0;
  let resynced = 0;
  const failures: ResyncFailure[] = [];
  const abandoned: ResyncAbandon[] = [];
  for (const capture of local) {
    if (existing.has(capture.id)) {
      await storage.remove(capture.id);
      alreadySynced++;
      continue;
    }
    const r = await syncOne(capture);
    if (r.ok) {
      await storage.remove(capture.id);
      resynced++;
    } else if (r.audioMissing) {
      // Audio local définitivement introuvable : réessayer est sans espoir. On
      // RETIRE la capture (fini la boucle + le spam de log) et on l'enregistre
      // comme abandon — distinct d'un échec transitoire.
      await storage.remove(capture.id);
      abandoned.push({ capture, error: r.error });
    } else {
      // Échec TRANSITOIRE (réseau, auth, RLS, taille…) : on GARDE la capture en
      // local (réessai au prochain trigger) ET on enregistre la cause → visible
      // dans le log du caller (fini le silence).
      failures.push({
        id: capture.id,
        error: r.error,
        ...(r.detail !== undefined ? { detail: r.detail } : {}),
      });
    }
  }
  return { alreadySynced, resynced, failed: failures.length, failures, abandoned };
}
